import React from 'react'
import { ActionDialog, Button, Divider, Icon, ListButton, Typography, icons, theme } from 'cdh-data-portal-theme'
import type { FilterFacet } from '../RegistryFilters.static'
import { useRegistryFiltersDialog } from './RegistryFiltersDialog.logic'
import type { SelectedFilterValues } from './RegistryFiltersDialog.logic'
import { useStyles } from './RegistryFiltersDialog.style'
import { localText } from './RegistryFiltersDialog.static'

export interface RegistryFiltersDialogProps {
  readonly isOpen: boolean
  readonly facets: readonly FilterFacet[]
  readonly selectedValues: SelectedFilterValues
  readonly onApply: (values: SelectedFilterValues) => void
  readonly onClose: () => void
}

export const RegistryFiltersDialog: React.FC<RegistryFiltersDialogProps> = ({
  isOpen,
  facets,
  selectedValues,
  onApply,
  onClose,
}) => {
  const { staged, focusFacetKey, setFocusFacetKey, hasChanges, selectFacetValue, applyAndClose, cancelAndClose } =
    useRegistryFiltersDialog({ isOpen, selectedValues, onApply, onClose })
  const { classes } = useStyles()

  const focusFacet = facets.find((facet) => facet.key === focusFacetKey) ?? null

  return (
    <ActionDialog
      actionText={localText.apply}
      disableConfirm={!hasChanges}
      disabledConfirmTooltip={!hasChanges ? localText.noChangesTooltip : undefined}
      isOpen={isOpen}
      onAccept={applyAndClose}
      onCancel={cancelAndClose}
      title={
        focusFacet ? (
          <Button
            icon={icons.chevronLeft}
            onClick={() => setFocusFacetKey(null)}
            title={localText.back}
            variant="text"
          />
        ) : (
          localText.title
        )
      }
    >
      <div className={classes.root}>{focusFacet ? renderFacetOptions(focusFacet) : renderFacetMenu()}</div>
    </ActionDialog>
  )

  function renderFacetMenu() {
    return facets.map((facet, index) => (
      <ListButton
        key={facet.key}
        index={index}
        icon={facet.icon}
        label={facet.title}
        rightIcon={<Icon color={theme.textColors.primary} iconPath={icons.chevronRight} />}
        secondaryLabel={facet.options.find((option) => option.id === staged[facet.key])?.name}
        width="100%"
        onClick={() => setFocusFacetKey(facet.key)}
      />
    ))
  }

  function renderFacetOptions(facet: FilterFacet) {
    return (
      <>
        <div className={classes.facetHeader}>
          <Icon color={theme.textColors.primaryLighter} iconPath={facet.icon} size={24} />
          <Typography variant="headline3">{facet.title}</Typography>
          <Divider vertical margin={{ top: 0, right: 12, bottom: 0, left: 12 }} />
          <Button
            icon={icons.close}
            isDisabled={!staged[facet.key]}
            onClick={() => selectFacetValue(facet.key, '')}
            title={localText.clear}
            variant="text"
          />
        </div>
        {facet.options.map((option, index) => (
          <ListButton
            key={option.id}
            index={index}
            label={option.name}
            rightIcon={
              staged[facet.key] === option.id ? (
                <Icon color={theme.colors.primary} iconPath={icons.checkMarkCompact} />
              ) : undefined
            }
            width="100%"
            onClick={() => selectFacetValue(facet.key, option.id)}
          />
        ))}
      </>
    )
  }
}
